import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useInView, useMotionValueEvent, useReducedMotion, useScroll, useSpring, useTransform } from "framer-motion";
import {
  ArrowRight,
  BookOpen,
  BrainCircuit,
  CandlestickChart,
  Compass,
  Crown,
  Gift,
  LineChart,
  Lock,
  Map,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import PublicNavbar from "@/components/PublicNavbar";
import PublicFooter from "@/components/PublicFooter";
import { OrbitalMark, Parallax, ScrollRail, SealMark, tierFactor, useMotionTier } from "@/components/landing/ScrollExperience";
import { LearnHubStage, MemberToolsStage } from "@/components/landing/ProductMotionStages";
import api from "@/lib/api";

const API = process.env.REACT_APP_BACKEND_URL;

const IMAGES = {
  hero: "https://static.prod-images.emergentagent.com/jobs/14a86ffc-498a-4ed2-b0cc-7fdfee251876/images/6bb7fa0e38174836deba20f7f3f3091df11625e802fe6d03bd7614d99b196a53.jpeg",
  ai: "https://static.prod-images.emergentagent.com/jobs/14a86ffc-498a-4ed2-b0cc-7fdfee251876/images/9ecd0e31a4636521b9bfe096c84f64f3f55a84d55496cf2d0e7cf3cfb4a92a6c.jpeg",
  market: "https://static.prod-images.emergentagent.com/jobs/14a86ffc-498a-4ed2-b0cc-7fdfee251876/images/9981a38c1f8ba59540811c2f8afeb603c4d4317c7b62e93b739b10e2b724eeec.jpeg",
  rewardMerchandise: "https://static.prod-images.emergentagent.com/jobs/14a86ffc-498a-4ed2-b0cc-7fdfee251876/images/a042a669a4abc80e2c7c56c779f5049c23fd3f5512edcb0d2c25076c17142b0b.jpeg",
  rewardDevices: "https://static.prod-images.emergentagent.com/jobs/14a86ffc-498a-4ed2-b0cc-7fdfee251876/images/694d2dd74f1fb667d45bfc65ff686d132972bf85eaaba1f405275785fecad727.jpeg",
  rewardExclusive: "https://static.prod-images.emergentagent.com/jobs/14a86ffc-498a-4ed2-b0cc-7fdfee251876/images/3f376cd82056c7d7526e873796ea952aaaa44c0810e6ac6bd0307b30f07122b8.jpeg",
  rewardExperiences: "https://static.prod-images.emergentagent.com/jobs/14a86ffc-498a-4ed2-b0cc-7fdfee251876/images/6cd3d3c219f74a35bb5cc33b04439b88a167f46fd1f509574644387bcdcaf483.jpeg",
};

const DEFAULTS = {
  hero_title: "Trade Where Precision Meets Prestige",
  hero_subtitle:
    "PLUSMAKER is a private trading ecosystem engineered for elite performance — institutional-grade indicators, an exclusive rewards architecture, and an AI market analyst built for the modern trader.",
  about_title: "Built for the Disciplined Few",
  about_body:
    "PLUSMAKER was founded on a single conviction: serious traders deserve serious infrastructure. Our ecosystem combines precision-engineered market indicators, a merit-based rewards program, and proprietary AI analysis — governed by the security and discretion of a private institution.",
  products: [],
};

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7, ease: "easeOut" },
};

/* Stagger variants for sequenced reveals (eyebrow → headline → copy) */
const staggerParent = { hidden: {}, show: { transition: { staggerChildren: 0.13, delayChildren: 0.05 } } };
const staggerChild = {
  hidden: { opacity: 0, y: 22 },
  show: { opacity: 1, y: 0, transition: { duration: 0.65, ease: "easeOut" } },
};

function SectionHeading({ eyebrow, title, body, testId, center = false }) {
  return (
    <motion.div
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-80px" }}
      variants={staggerParent}
      className={center ? "max-w-3xl mx-auto text-center" : "max-w-3xl"}
    >
      <motion.p variants={staggerChild} className="eyebrow-label mb-4">{eyebrow}</motion.p>
      <motion.h2
        variants={staggerChild}
        className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white"
        data-testid={`${testId}-title`}
      >
        {title}
      </motion.h2>
      {body && (
        <motion.p variants={staggerChild} className="mt-5 text-base md:text-lg text-[#94A3B8] leading-relaxed" data-testid={`${testId}-body`}>
          {body}
        </motion.p>
      )}
    </motion.div>
  );
}

/* Shared cinematic frame: generated visual + grid, scan sweep, vignette, gold border */
function CinematicPanel({ src, alt, children, className = "", testId }) {
  return (
    <div className={`card-lux gold-glow-frame overflow-hidden relative ${className}`} data-testid={testId}>
      <img src={src} alt={alt} className="absolute inset-0 w-full h-full object-cover" loading="eager" />
      <div className="absolute inset-0 lp-grid opacity-25" />
      <div className="lp-scan" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#050506]/80 via-transparent to-[#050506]/30" />
      <div className="lp-sheen" />
      {children}
    </div>
  );
}

/* Floating intelligence label — presentation only, cycles slowly */
function IntelLabel({ text, pos = "", delay, testId }) {
  return (
    <span
      className={`${pos ? `absolute ${pos}` : "relative"} lp-intel flex items-center gap-1.5 font-mono text-[8px] sm:text-[9px] tracking-[0.22em] text-[#D4AF37] bg-[#050506]/85 border border-[#D4AF37]/30 rounded-full px-2.5 py-1 backdrop-blur-sm`}
      style={{ animationDelay: `${delay}s` }}
      data-testid={testId}
    >
      <span className="h-1 w-1 rounded-full bg-[#D4AF37]" />
      {text}
    </span>
  );
}

/* Approved PlusMaker monogram — the EXACT supplied gold monogram artwork,
 *  used as a local transparent image asset. Never redrawn or reinterpreted.
 *  Allowed treatment only: scale, position, subtle shadow/glow. */
export const PM_MONOGRAM_SRC = `${process.env.PUBLIC_URL}/assets/pm-monogram.png`;

/* Dedicated hero emblem logo — the supplied gold PLUSMAKER cross wordmark.
 * Separate asset (does NOT overwrite the shared pm-monogram.png). Source PNG has a
 * black field, so it's composited with mix-blend screen to drop the black cleanly. */
export const PM_HERO_LOGO_SRC = `${process.env.PUBLIC_URL}/assets/pm-hero-logo.png`;

/* Approved "Obsidian Spotlight" stage backdrop (610x735): champagne light beam, low
 * obsidian platform with gold edge, fine floor candles and floor reflection. Decorative
 * only — the logo and slogan are real elements layered on top. */
export const PM_HERO_STAGE_SRC = `${process.env.PUBLIC_URL}/assets/hero/pm-hero-stage.webp`;

/* Illustrative display values — NOT a live market feed. Static, reused values. */
const TICKER_ITEMS = [
  { s: "BTC/USD", p: "97,420", c: "+2.31%", up: true },
  { s: "US30", p: "44,210", c: "+0.45%", up: true },
  { s: "GBP/JPY", p: "192.44", c: "+0.18%", up: true },
  { s: "NAS100", p: "21,090", c: "+1.02%", up: true },
  { s: "ETH/USD", p: "3,842", c: "+1.67%", up: true },
  { s: "USD/JPY", p: "155.32", c: "-0.21%", up: false },
  { s: "XAU/USD", p: "2,312.48", c: "+0.04%", up: true },
  { s: "EUR/USD", p: "1.0845", c: "+0.09%", up: true },
];

/* Illustrative bottom ticker — continuous scroll on desktop/mobile; static under reduced-motion. */
function HeroTicker() {
  const reduce = useReducedMotion();
  const Row = ({ ariaHidden }) => (
    <div className="flex items-center gap-7 pr-7 shrink-0" aria-hidden={ariaHidden}>
      {TICKER_ITEMS.map((t, i) => (
        <span key={i} className="flex items-center gap-2 font-mono text-[11px] whitespace-nowrap">
          <span className="text-[#9aa3b2]">{t.s}</span>
          <span className="text-white">{t.p}</span>
          <span className={t.up ? "text-emerald-400" : "text-red-400"}>{t.c}</span>
        </span>
      ))}
    </div>
  );
  return (
    <div className="relative w-full overflow-hidden border-y border-[#1b1e27] bg-black/60 py-2.5" data-testid="hero-ticker">
      {reduce ? (
        <div className="flex items-center gap-7 pl-7 pr-7"><Row /></div>
      ) : (
        <div className="flex w-max hero-ticker-track pl-7"><Row /><Row ariaHidden /></div>
      )}
    </div>
  );
}

/* Hero emblem — the exact supplied monogram (primary focal). No pedestal.
 * The gold drop-shadow is derived from the PNG's own alpha, so the faint shadow
 * beneath is literally the shape of the logo — subtle, never an oval spotlight. */
function PMEmblemMark({ className = "h-44 sm:h-52 w-auto" }) {
  return (
    <img
      src={PM_MONOGRAM_SRC}
      alt="PlusMaker gold monogram"
      data-testid="hero-emblem-mark"
      draggable="false"
      className={`select-none ${className}`}
      style={{ filter: "drop-shadow(0 20px 26px rgba(0,0,0,.55)) drop-shadow(0 14px 34px rgba(212,175,55,.26))" }}
    />
  );
}

/* Hero stage — "Obsidian Spotlight": rich black with restrained champagne light
 *  from above, the large gold PLUSMAKER logo as focus, subtle gold floor candles,
 *  and a low obsidian display platform with a faint reflection. The logo performs a
 *  single zoom-in on first load then stays still, honouring reduced-motion. */
function HeroStage() {
  const reduce = useReducedMotion();
  const logoRef = useRef(null);
  const inView = useInView(logoRef, { once: true, amount: 0.35 });
  const [loaded, setLoaded] = useState(false);
  useEffect(() => { if (logoRef.current?.complete) setLoaded(true); }, []);
  const show = reduce || (inView && loaded);
  return (
    <div className="hero-stage relative w-full overflow-hidden" data-testid="hero-market-visual">
      {/* backdrop: beam, obsidian platform, floor candles + reflection (mobile crops the upper beam) */}
      <img src={PM_HERO_STAGE_SRC} alt="" aria-hidden="true" draggable="false"
        className="hero-stage-bg absolute left-0 w-full h-auto max-w-none select-none pointer-events-none" />

      {/* TRADE • ELEVATE • REDEEM — centred above the logo */}
      <p className="hero-stage-slogan absolute left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 font-mono text-[11px] sm:text-xs tracking-[0.34em] text-[#E7CF9B] whitespace-nowrap" data-testid="hero-pillars">
        TRADE<span className="text-[#D4AF37] mx-2">•</span>ELEVATE<span className="text-[#D4AF37] mx-2">•</span>REDEEM
      </p>

      {/* exact gold PLUSMAKER logo — fade+zoom once, when first in view AND the image is ready */}
      <motion.div
        className="hero-stage-logo absolute left-1/2 z-10"
        initial={reduce ? { opacity: 1, scale: 1, x: "-50%" } : { opacity: 0, scale: 0.94, x: "-50%" }}
        animate={show ? { opacity: 1, scale: 1, x: "-50%" } : undefined}
        transition={reduce ? { duration: 0 } : { duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        data-testid="hero-emblem"
        data-entered={show ? "true" : "false"}
      >
        <img ref={logoRef} src={PM_HERO_LOGO_SRC} alt="PLUSMAKER" data-testid="hero-emblem-mark" draggable="false"
          onLoad={() => setLoaded(true)}
          className="block w-full h-auto max-w-none select-none"
          style={{ filter: "drop-shadow(0 14px 26px rgba(0,0,0,.55)) drop-shadow(0 6px 28px rgba(212,175,55,.22))" }} />
      </motion.div>
      <div aria-hidden="true" className="hero-stage-platform absolute" data-testid="hero-platform" />
    </div>
  );
}

function MarketVisual() {
  return (
    <CinematicPanel src={IMAGES.market} alt="Professional trading environment" className="h-[300px] sm:h-[380px] lg:h-[440px]" testId="market-environment-visual">
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-60px" }}
        variants={{ hidden: {}, show: { transition: { staggerChildren: 0.18, delayChildren: 0.25 } } }}
        className="absolute top-4 left-4 flex flex-wrap gap-2"
      >
        {["ASIA", "LONDON", "NEW YORK"].map((s, i) => (
          <motion.span
            key={s}
            variants={{ hidden: { opacity: 0, y: 10 }, show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } } }}
            className="font-mono text-[9px] tracking-[0.2em] uppercase px-2.5 py-1 rounded-full border border-[#222530] bg-[#050506]/85 text-[#64748B] flex items-center gap-1.5 backdrop-blur-sm"
          >
            <span className={`h-1 w-1 rounded-full ${i === 1 ? "bg-[#D4AF37]" : "bg-[#3a3d4d]"}`} />
            {s} SESSION
          </motion.span>
        ))}
      </motion.div>
      <IntelLabel text="MARKET STRUCTURE" pos="top-[30%] left-[6%]" delay={0.6} testId="market-label-0" />
      <IntelLabel text="LIQUIDITY" pos="top-[56%] right-[8%]" delay={2.8} testId="market-label-1" />
      <IntelLabel text="MOMENTUM" pos="top-[14%] right-[18%]" delay={5.2} testId="market-label-2" />
      <IntelLabel text="PRECISION" pos="bottom-[12%] left-[20%]" delay={7.6} testId="market-label-3" />
    </CinematicPanel>
  );
}

/* PlusMaker Intelligence — "Coming Soon" presentation stage. A native animated
 *  motion graphic (no active AI, no analysis, no data): a breathing PM core, slowly
 *  rotating orbital rings, travelling light trails/pulses, gently floating Explore /
 *  Learn / Navigate nodes, drifting particles and slow background waves over a
 *  podium. `animated=false` (prefers-reduced-motion) renders a clean static frame. */
const AI_ORBIT_CSS = `
  .pm-ai-orbit-layer { position:absolute; left:50%; top:50%; transform:translate(-50%,-50%); width:200px; height:84px; pointer-events:none; z-index:2; }
  .pm-ai-orbit-node { position:absolute; left:0; top:0; offset-anchor:center; offset-rotate:0deg; offset-distance:var(--d,0%); offset-path:path('M 4 42 A 96 38 0 0 1 196 42 A 96 38 0 0 1 4 42 Z'); animation:pmAiOrbit 46s linear infinite; will-change:offset-distance; }
  .pm-ai-orbit-node.n2 { animation-delay:-15.33s; }
  .pm-ai-orbit-node.n3 { animation-delay:-30.66s; }
  @keyframes pmAiOrbit { from { offset-distance:0%; } to { offset-distance:100%; } }
  @media (min-width:768px) {
    .pm-ai-orbit-layer { width:520px; height:310px; }
    .pm-ai-orbit-node { offset-path:path('M 10 155 A 250 150 0 0 1 510 155 A 250 150 0 0 1 10 155 Z'); }
  }
  @media (prefers-reduced-motion: reduce) {
    .pm-ai-orbit-node { animation:none !important; }
  }
`;

const AI_NODES = [
  { key: "explore", label: "Explore", d: "0%", icon: <Compass className="h-5 w-5 sm:h-6 sm:w-6" /> },
  { key: "learn", label: "Learn", d: "33%", icon: <BookOpen className="h-5 w-5 sm:h-6 sm:w-6" /> },
  { key: "navigate", label: "Navigate", d: "66%", icon: <Map className="h-5 w-5 sm:h-6 sm:w-6" /> },
];

function AIComingSoonStage({ animated = true }) {
  const A = animated;
  return (
    <div className="relative w-full max-w-5xl mx-auto mt-10 aspect-[1000/560]" data-testid="ai-coming-soon-stage">
      <svg viewBox="0 0 1000 560" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid meet" aria-hidden="true" focusable="false">
        <defs>
          <radialGradient id="aics-core" cx="50%" cy="50%" r="50%">
            <stop stopColor="#FCE9A6" stopOpacity="0.5" />
            <stop offset="0.4" stopColor="#D4AF37" stopOpacity="0.22" />
            <stop offset="1" stopColor="#D4AF37" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="aics-mono" cx="42%" cy="34%" r="72%">
            <stop stopColor="#FFF1C5" stopOpacity="0.5" />
            <stop offset="0.5" stopColor="#D4AF37" stopOpacity="0.14" />
            <stop offset="1" stopColor="#D4AF37" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="aics-wave" x1="0" y1="0" x2="1" y2="0">
            <stop stopColor="#D4AF37" stopOpacity="0" />
            <stop offset="0.5" stopColor="#D4AF37" stopOpacity="0.4" />
            <stop offset="1" stopColor="#D4AF37" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* background wave lines — very slow drift */}
        <g fill="none" stroke="url(#aics-wave)" strokeWidth="1.2">
          <path d="M-40 150 C 150 90 300 210 520 150 S 900 90 1040 160" className={A ? "gpu-drift" : undefined} style={A ? { animationDuration: "7s" } : undefined} />
          <path d="M-40 430 C 180 490 360 360 560 430 S 880 500 1040 420" className={A ? "gpu-drift" : undefined} style={A ? { animationDuration: "9s", animationDelay: "1s" } : undefined} />
        </g>

        {/* podium — concentric base rings */}
        <g transform="translate(500 476)">
          {[340, 268, 198, 128].map((rx, i) => (
            <ellipse key={rx} cx="0" cy="0" rx={rx} ry={rx * 0.18} fill="none" stroke="rgba(212,175,55,0.26)" strokeWidth="1"
              className={A ? "gpu-breathe" : undefined} style={A ? { animationDuration: `${5 + i}s`, animationDelay: `${i * 0.4}s` } : undefined} />
          ))}
        </g>

        {/* orbital rings — slow rotation */}
        <g className={A ? "gpu-spin" : undefined} style={A ? { transformOrigin: "500px 300px", animationDuration: "64s" } : undefined}>
          <ellipse cx="500" cy="300" rx="250" ry="150" fill="none" stroke="rgba(212,175,55,0.3)" strokeWidth="1" strokeDasharray="4 10" />
        </g>
        <g className={A ? "gpu-spin-rev" : undefined} style={A ? { transformOrigin: "500px 300px", animationDuration: "92s" } : undefined}>
          <ellipse cx="500" cy="300" rx="300" ry="118" fill="none" stroke="rgba(212,175,55,0.16)" strokeWidth="1" />
        </g>
        <g className={A ? "gpu-spin" : undefined} style={A ? { transformOrigin: "500px 300px", animationDuration: "120s" } : undefined}>
          <ellipse cx="500" cy="300" rx="196" ry="182" fill="none" stroke="rgba(212,175,55,0.12)" strokeWidth="1" strokeDasharray="2 9" />
        </g>

        {/* light trails / pulses travelling the orbit */}
        {A && [0, 1, 2].map((n) => (
          <g key={n} className="gpu-spin" style={{ transformOrigin: "500px 300px", animationDuration: `${16 + n * 6}s`, animationDelay: `${-n * 4}s` }}>
            <circle cx={n === 0 ? 750 : n === 1 ? 250 : 620} cy={n === 0 ? 300 : n === 1 ? 300 : 156} r={n === 0 ? 3.4 : 2.4} fill="#FCE9A6" />
          </g>
        ))}

        {/* core — breathing glow + rings + monogram */}
        <circle cx="500" cy="300" r="150" fill="url(#aics-core)" className={A ? "gpu-breathe" : undefined} />
        {A && <circle cx="500" cy="300" r="60" fill="none" stroke="rgba(212,175,55,0.4)" strokeWidth="1.2" className="gpu-pulse-ring" />}
        <circle cx="500" cy="300" r="96" fill="rgba(212,175,55,0.04)" stroke="rgba(212,175,55,0.45)" strokeWidth="1" />
        <circle cx="500" cy="300" r="78" fill="url(#aics-mono)" stroke="rgba(212,175,55,0.6)" strokeWidth="1.2" className={A ? "gpu-breathe" : undefined} />
        {/* abstract gold intelligence core — non-logo radiating scanner spokes + orb */}
        {A && (
          <g className="gpu-spin" style={{ transformOrigin: "500px 300px", animationDuration: "26s" }}>
            {[0, 45, 90, 135, 180, 225, 270, 315].map((a) => (
              <line key={a} x1="500" y1="252" x2="500" y2="272" stroke="rgba(212,175,55,0.4)" strokeWidth="1.6" strokeLinecap="round" transform={`rotate(${a} 500 300)`} />
            ))}
          </g>
        )}
        <circle cx="500" cy="300" r="34" fill="url(#aics-core)" className={A ? "gpu-breathe" : undefined} />
        <circle cx="500" cy="300" r="14" fill="rgba(255,240,194,0.92)" />

        {/* drifting particles */}
        {[[180, 120], [820, 140], [120, 340], [880, 330], [300, 92], [720, 150], [700, 470], [270, 470]].map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r={i % 2 ? 1.8 : 1.1} fill="#D4AF37" opacity="0.5"
            className={A ? "gpu-drift" : undefined} style={A ? { animationDuration: `${4 + i}s`, animationDelay: `${i * 0.4}s` } : undefined} />
        ))}
      </svg>

      <style>{AI_ORBIT_CSS}</style>

      {/* Explore / Learn / Navigate — continuous clockwise orbital motion, upright labels */}
      <div className="pm-ai-orbit-layer" data-testid="ai-orbit-layer">
        {AI_NODES.map((n, i) => (
          <div
            key={n.key}
            className={`pm-ai-orbit-node ${i === 1 ? "n2" : i === 2 ? "n3" : ""} flex flex-col items-center`}
            style={{ "--d": n.d }}
            data-testid={`ai-node-${n.key}`}
          >
            <span className="flex h-11 w-11 sm:h-14 sm:w-14 items-center justify-center rounded-full border border-[#D4AF37]/50 bg-[#0B0C10]/70 backdrop-blur-sm text-[#E9C766] shadow-[0_0_22px_rgba(212,175,55,0.18)]">
              {n.icon}
            </span>
            <span className="mt-1.5 font-mono text-[10px] sm:text-[11px] tracking-[0.16em] uppercase text-[#CBD5E1] whitespace-nowrap">{n.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* Reward card — collection assembly: alternating entrance depths, pedestal glow,
 *  subtle in-frame image parallax */
function RewardCard({ card, i, tier }) {
  const ref = useRef(null);
  const tf = tierFactor(tier);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const raw = useTransform(scrollYProgress, [0, 1], [14 * tf, -14 * tf]);
  const y = useSpring(raw, { stiffness: 70, damping: 22, mass: 0.6 });
  const deep = i % 2 === 1;
  return (
    <div ref={ref} className="relative">
      <div aria-hidden="true" className="absolute -bottom-6 left-1/2 -translate-x-1/2 h-14 w-3/4 bg-[#D4AF37]/[0.06] blur-2xl rounded-full pointer-events-none" />
      <motion.div
        initial={{ opacity: 0, y: deep ? 64 : 40, scale: deep ? 0.9 : 0.94 }}
        whileInView={{ opacity: 1, y: 0, scale: 1 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.75, delay: i * 0.14, ease: "easeOut" }}
        className="card-lux lp-reward-card overflow-hidden group relative"
        data-testid={`rewards-card-${i}`}
      >
        <div className="relative h-40 sm:h-44 overflow-hidden">
          <motion.div className="absolute inset-0" style={tf ? { y, scale: 1.12 } : undefined}>
            <img
              src={card.img}
              alt={card.title}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
              loading="lazy"
            />
          </motion.div>
          <div className="absolute inset-0 bg-gradient-to-t from-[#101117] via-transparent to-transparent pointer-events-none" />
        </div>
        <div className="p-6">
          <h3 className="font-heading text-base font-semibold text-white">{card.title}</h3>
          <p className="mt-2.5 text-sm text-[#94A3B8] leading-relaxed">{card.body}</p>
          <p className="mt-5 text-[10px] font-mono tracking-[0.2em] uppercase text-[#64748B]">
            Full reveal at launch
          </p>
        </div>
      </motion.div>
    </div>
  );
}

export default function LandingPage() {
  const [content, setContent] = useState(DEFAULTS);
  const navigate = useNavigate();
  const tier = useMotionTier();
  const tf = tierFactor(tier);

  /* Hero — content lifts gently, stage drifts down (opening-the-door feel) */
  const heroRef = useRef(null);
  const { scrollYProgress: heroP } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroContentY = useTransform(heroP, [0, 1], [0, -34 * tf]);
  const heroVisualY = useTransform(heroP, [0, 1], [0, 30 * tf]);
  const heroContentYSpring = useSpring(heroContentY, { stiffness: 70, damping: 22, mass: 0.6 });
  const heroVisualYSpring = useSpring(heroVisualY, { stiffness: 70, damping: 22, mass: 0.6 });

  /* Rank progression — gold line draws forward, nodes illuminate as it passes */
  const rankRef = useRef(null);
  const { scrollYProgress: rankP } = useScroll({ target: rankRef, offset: ["start 85%", "end 40%"] });
  const rankLineScale = useTransform(rankP, [0, 1], [0, 1]);
  const [rankActive, setRankActive] = useState(tier < 0 ? 8 : 0);
  useMotionValueEvent(rankP, "change", (v) => {
    if (tier >= 0) setRankActive(Math.min(8, Math.floor(v * 8.99)));
  });

  /* How it works — connector line activates with scroll */
  const howRef = useRef(null);
  const { scrollYProgress: howP } = useScroll({ target: howRef, offset: ["start 85%", "end 45%"] });
  const howLineScale = useTransform(howP, [0, 1], [0, 1]);

  /* TZB product — slow cinematic push-in on the product image */
  const productRef = useRef(null);
  const { scrollYProgress: productP } = useScroll({ target: productRef, offset: ["start end", "end start"] });
  const productImgScale = useTransform(productP, [0, 1], [1.02, 1.08]);

  useEffect(() => {
    api
      .get("/site-content")
      .then((res) => {
        if (res.data && res.data.hero_title) setContent({ ...DEFAULTS, ...res.data });
      })
      .catch(() => {});
  }, []);

  /* Reduced motion — everything rests in its fully revealed state */
  useEffect(() => {
    if (tier < 0) {
      setRankActive(8);
    }
  }, [tier]);

  /* Indicator product — the member-ecosystem technical-analysis tool. The
   * section always presents the compliant identity regardless of CMS naming. */

  return (
    <div className="bg-[#050506] min-h-screen" data-testid="landing-page">
      <PublicNavbar />
      <ScrollRail />

      {/* SECTION 1 — HERO (approved mockup composition) */}
      <section ref={heroRef} className="relative pt-24 pb-14 lg:pt-40 lg:pb-20 overflow-hidden" data-testid="hero-section">
        <div aria-hidden="true" className="absolute top-0 left-1/2 -translate-x-1/2 h-[520px] w-[920px] bg-[#D4AF37]/[0.06] blur-[150px] rounded-full pointer-events-none lp-ambient" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="relative flex flex-col lg:grid lg:grid-cols-[1.05fr_1.2fr] lg:grid-rows-[auto_auto_auto_auto_auto] lg:gap-x-12 lg:gap-y-5 lg:items-start">
            {/* A — eyebrow + headline */}
            <motion.div
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="order-1 lg:col-start-1 lg:row-start-1 lg:pt-4"
              style={tf ? { y: heroContentYSpring } : undefined}
            >
              <p className="eyebrow-label mb-4 sm:mb-5">Private Trading Ecosystem</p>
              <h1
                className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05]"
                data-testid="hero-title"
              >
                Trade Where Precision <span className="text-gold-gradient">Meets Prestige</span>
              </h1>
            </motion.div>

            {/* C — chart scene + new logo (right column; order-2 on mobile) */}
            <motion.div
              className="order-2 mt-5 lg:mt-0 lg:absolute lg:inset-y-0 lg:right-0 lg:w-[52%] lg:flex lg:items-center"
              style={tf ? { y: heroVisualYSpring } : undefined}
            >
              <HeroStage />
            </motion.div>

            {/* B — full paragraph */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1, ease: "easeOut" }}
              className="order-3 mt-5 lg:mt-0 lg:col-start-1 lg:row-start-2 max-w-xl"
            >
              <p className="text-base md:text-lg text-[#94A3B8] leading-relaxed" data-testid="hero-subtitle">
                PLUSMAKER brings trading tools, exclusive rewards, lucky draws and achievement certificates together in one connected ecosystem — with learning resources, a personalised dashboard and a structured rank journey designed to recognise your progress.
              </p>
            </motion.div>

            {/* D — CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
              className="order-4 mt-7 lg:mt-1 lg:col-start-1 lg:row-start-3"
            >
              <div className="hidden sm:flex gap-4">
                <button onClick={() => navigate("/register")} data-testid="hero-register-button" className="btn-gold px-8 py-3.5 text-sm flex items-center justify-center gap-2">
                  Claim Your Access <ArrowRight className="h-4 w-4" />
                </button>
                <button onClick={() => navigate("/login")} data-testid="hero-login-button" className="btn-outline-gold px-8 py-3.5 text-sm">
                  Member Login
                </button>
              </div>
              <div className="sm:hidden flex flex-col items-stretch gap-3">
                <button onClick={() => navigate("/register")} data-testid="hero-register-button-mobile" className="btn-gold w-full py-3.5 text-sm flex items-center justify-center gap-2">
                  Claim Your Access <ArrowRight className="h-4 w-4" />
                </button>
                <button onClick={() => navigate("/login")} data-testid="hero-login-button-mobile" className="btn-outline-gold w-full py-3.5 text-sm">
                  Member Login
                </button>
              </div>
            </motion.div>

            {/* E — membership + trust */}
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
              className="order-5 mt-7 lg:mt-2 lg:col-start-1 lg:row-start-4"
            >
              <p className="font-mono text-[10px] sm:text-xs tracking-[0.22em] text-[#D4AF37]">PRIVATE MEMBERSHIP · TOOLS · AI · REWARDS</p>
              <p className="mt-2 flex items-center gap-2 text-xs text-[#94A3B8]"><ShieldCheck className="h-3.5 w-3.5 text-[#D4AF37] shrink-0" /> Referral-only membership · Exclusive to members within the PLUSMAKER network only</p>
            </motion.div>

            {/* F — four feature icons */}
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
              className="order-6 mt-6 lg:mt-2 lg:col-start-1 lg:row-start-5 border-t border-[#222530] pt-4"
            >
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-4" data-testid="hero-feature-strip">
                {[
                  { icon: LineChart, l1: "Institutional", l2: "Indicators" },
                  { icon: BrainCircuit, l1: "AI Market", l2: "Analysis" },
                  { icon: Gift, l1: "Exclusive", l2: "Rewards" },
                  { icon: Lock, l1: "Private", l2: "Membership" },
                ].map((f, i) => (
                  <div key={i} className="flex items-start gap-2.5" data-testid={`hero-feature-${i}`}>
                    <f.icon className="h-5 w-5 text-[#D4AF37] shrink-0 mt-0.5" />
                    <span className="text-xs leading-tight text-[#94A3B8]"><span className="block text-white font-medium">{f.l1}</span>{f.l2}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

        </div>
        <div className="mt-10 lg:mt-14">
          <HeroTicker />
        </div>
      </section>

      {/* SECTION 2 — TRADING ENVIRONMENT */}
      <section id="about" className="py-20 lg:py-28" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <SectionHeading eyebrow="Trading Environment" title={content.about_title} body={content.about_body} testId="about" />
              <motion.div
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-80px" }}
                variants={{ hidden: {}, show: { transition: { staggerChildren: 0.14, delayChildren: 0.3 } } }}
                className="mt-8 flex flex-wrap gap-x-8 gap-y-3"
              >
                {["MARKET STRUCTURE", "LIQUIDITY", "MOMENTUM", "PRECISION"].map((l) => (
                  <motion.span
                    key={l}
                    variants={{ hidden: { opacity: 0, y: 14 }, show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: "easeOut" } } }}
                    className="flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] text-[#64748B]"
                  >
                    <span className="h-px w-5 bg-[#D4AF37]/60" />
                    {l}
                  </motion.span>
                ))}
              </motion.div>
            </div>
            <Parallax range={18} tier={tier}>
              <motion.div {...fadeUp}>
                <MarketVisual />
              </motion.div>
            </Parallax>
          </div>
        </div>
      </section>

      {/* SECTION 3 — MEMBER TOOLS (Indicator + Automated Trading) */}
      <section id="products" className="py-20 lg:py-28 bg-[#0B0C10]" data-testid="products-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Unified Member Tools composition — Indicator ⟶ Automated Trading in one animated visual */}
          <motion.div
            ref={productRef}
            initial={{ opacity: 0, y: 44 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-70px" }}
            transition={{ duration: 0.85, ease: "easeOut" }}
            className="grid lg:grid-cols-[1fr_1.3fr] gap-8 lg:gap-12 items-center max-w-6xl mx-auto"
            data-testid="product-item-0"
          >
            <div className="order-2 lg:order-1">
              <div className="flex items-center gap-4">
                <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#D4AF37]" data-testid="product-badge-0">
                  Member Tools
                </p>
                <span aria-hidden="true" className="h-px flex-1 max-w-[130px] bg-gradient-to-r from-[#D4AF37]/50 to-transparent" />
              </div>
              <h3 className="mt-6 font-heading text-4xl sm:text-5xl font-bold text-white tracking-tight leading-[1.05]" data-testid="product-name-0">
                Indicator &amp;<br className="hidden sm:block" /> Automated Trading
              </h3>
              <p className="mt-6 text-lg text-[#94A3B8] leading-relaxed max-w-md" data-testid="product-description-0">
                Precision tools and structured automation designed for eligible PlusMaker members.
              </p>
              <p className="mt-8 font-mono text-[11px] tracking-[0.28em] uppercase text-[#D4AF37]/80" data-testid="product-footer-0">
                Technical Analysis • Member Access
              </p>
            </div>
            <motion.div
              className="order-1 lg:order-2 relative h-72 sm:h-96 lg:h-[440px]"
              style={tf ? { scale: productImgScale } : undefined}
              data-testid="product-image-0"
            >
              <MemberToolsStage animated={tier >= 0} />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* SECTION 4 — LIVE & LEARN (standalone education hub destination) */}
      <section id="live-learn" className="ll-home" aria-labelledby="ll-home-title" data-testid="live-learn-section">
        <div className="ll-home__layout">
          <div className="ll-home__copy" data-testid="ll-copy">
            <p className="ll-home__eyebrow" data-testid="ll-eyebrow">EDUCATION HUB</p>
            <h2 id="ll-home-title" className="ll-home__title font-heading" data-testid="ll-title">
              Live &amp; Learn
            </h2>
            <p className="ll-home__description" data-testid="ll-description">
              Curated education, live sessions and expert-led resources designed to help members build stronger market understanding.
            </p>
            <button
              type="button"
              onClick={() => navigate("/member/live-learn")}
              className="ll-home__cta"
              data-testid="ll-cta"
            >
              Enter Live &amp; Learn
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </button>
            <p className="ll-home__footer" data-testid="ll-footer">EDUCATIONAL CONTENT • MEMBER ECOSYSTEM</p>
            <p className="ll-home__disclaimer" data-testid="ll-disclaimer">
              Educational content only — not financial advice.
            </p>
          </div>
          <LearnHubStage animated={tier >= 0} />
        </div>
      </section>

      {/* SECTION 5 — PLUSMAKER AI (Coming Soon presentation) */}
      <section id="ai" className="py-20 lg:py-28 relative overflow-hidden bg-[#050506]" data-testid="ai-section">
        <div className="absolute inset-x-0 top-1/3 h-[420px] bg-[#D4AF37]/[0.05] blur-[130px] pointer-events-none" aria-hidden="true" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative text-center">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className="font-mono text-[11px] sm:text-xs tracking-[0.34em] uppercase text-[#D4AF37]"
            data-testid="ai-eyebrow"
          >
            PlusMaker Intelligence
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white"
            data-testid="ai-title"
          >
            A Smarter Way to Explore
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.12 }}
            className="mt-5 mx-auto max-w-xl text-base md:text-lg text-[#94A3B8] leading-relaxed"
            data-testid="ai-body"
          >
            An upcoming platform experience designed to help members navigate PlusMaker resources, tools and educational content.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-7 inline-flex items-center gap-2.5 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/[0.06] px-5 py-2"
            data-testid="ai-coming-soon"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[#D4AF37]" aria-hidden="true" />
            <span className="font-mono text-[10px] sm:text-[11px] tracking-[0.34em] uppercase text-[#E9C766]">Coming Soon</span>
            <span className="h-1.5 w-1.5 rounded-full bg-[#D4AF37]" aria-hidden="true" />
          </motion.div>
          <AIComingSoonStage animated={tier >= 0} />
          <p className="mt-6 font-mono text-[10px] sm:text-[11px] tracking-[0.3em] uppercase text-[#64748B]" data-testid="ai-footer">
            Platform Experience • In Development
          </p>
        </div>
      </section>

      {/* SECTION 6 — REWARDS */}
      <section id="rewards" className="py-20 lg:py-28" data-testid="rewards-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Rewards Ecosystem"
            title="Trade. Progress. Redeem."
            body="Achievement becomes tangible through a curated rewards ecosystem designed around progression."
            testId="rewards"
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 mt-14">
            {[
              { img: IMAGES.rewardMerchandise, title: "Premium Merchandise", body: "Curated PlusMaker merchandise — crafted pieces that carry the mark of progression." },
              { img: IMAGES.rewardDevices, title: "Devices & Technology", body: "Premium devices and technology selected for the modern trading desk." },
              { img: IMAGES.rewardExclusive, title: "Exclusive Products", body: "Limited, rank-gated pieces reserved for proven milestones." },
              { img: IMAGES.rewardExperiences, title: "Experiences", body: "Curated experiences that turn achievement into memory." },
            ].map((card, i) => (
              <RewardCard key={card.title} card={card} i={i} tier={tier} />
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 7 — UNLIMITED LUCKY DRAW (new cinematic moment, no gambling imagery) */}
      <section id="lucky-draw" className="py-20 lg:py-28 relative overflow-hidden" data-testid="lucky-draw-section">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 h-[380px] w-[680px] bg-[#D4AF37]/[0.05] blur-[130px] rounded-full pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <SectionHeading
                eyebrow="Unlimited Lucky Draw"
                title="Another Chance. Always Open."
                body="Inside the PlusMaker ecosystem, the Lucky Draw never closes. Eligible members always have an open opportunity — a continuous part of the rewards journey, built around progression."
                testId="lucky-draw"
              />
              <motion.p {...fadeUp} className="mt-7 font-mono text-[10px] tracking-[0.28em] uppercase text-[#D4AF37]/70" data-testid="lucky-draw-tag">
                Part of the PlusMaker Rewards Ecosystem
              </motion.p>
            </div>
            <motion.div {...fadeUp}>
              <OrbitalMark tier={tier} />
            </motion.div>
          </div>
        </div>
      </section>

      {/* SECTION 8 — 36-RANK PROGRESSION */}
      <section className="py-20 lg:py-28 bg-[#0B0C10] overflow-hidden" data-testid="rank-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Rank Progression"
            title="36 Ranks. One Trading Journey."
            body="Every milestone in your trading journey unlocks the next level of the PlusMaker ecosystem."
            testId="rank"
          />
          <motion.div {...fadeUp} className="mt-8 flex flex-wrap items-center justify-center gap-x-3 gap-y-2" data-testid="rank-flow-labels">
            {["TRADE", "PROGRESS", "ACHIEVE", "REDEEM"].map((s, i) => (
              <span key={s} className="flex items-center gap-3">
                <span className="font-mono text-[10px] tracking-[0.28em] text-[#D4AF37]">{s}</span>
                {i < 3 && <span className="text-[#3a3d4d] text-xs">→</span>}
              </span>
            ))}
          </motion.div>
          <motion.div {...fadeUp} className="mt-14 relative" data-testid="rank-track" ref={rankRef}>
            <div className="absolute top-[9px] left-0 right-0 h-px bg-[#222530]" />
            <motion.div
              className="absolute top-[9px] left-0 right-0 h-px origin-left bg-gradient-to-r from-[#D4AF37]/25 via-[#D4AF37]/85 to-[#D4AF37]/25"
              style={{ scaleX: rankLineScale }}
              data-testid="rank-progress-line"
            />
            <div className="absolute top-[9px] left-0 right-0 h-px lp-rank-line opacity-60" />
            <div className="relative grid grid-cols-4 sm:grid-cols-8 gap-y-10">
              {Array.from({ length: 8 }).map((_, i) => {
                const active = i < rankActive;
                const frontier = active && i === rankActive - 1;
                return (
                  <div key={i} className="flex flex-col items-center gap-3 group" data-testid={`rank-node-${i}`} data-active={active}>
                    <span
                      className={`lp-rank-node-state h-[19px] w-[19px] rounded-full border flex items-center justify-center transition-transform duration-300 group-hover:scale-125 ${
                        frontier
                          ? "border-[#D4AF37] bg-[#0B0C10] lp-rank-frontier"
                          : active
                            ? "border-[#D4AF37] bg-[#0B0C10] shadow-[0_0_14px_rgba(212,175,55,.45)] lp-rank-node"
                            : "border-[#D4AF37]/25 bg-[#0B0C10]"
                      }`}
                      style={active ? { animationDelay: `${i * 0.55}s` } : undefined}
                    >
                      <Lock className={`h-2 w-2 transition-colors duration-500 ${active ? "text-[#D4AF37]" : "text-[#D4AF37]/40"}`} />
                    </span>
                    <span className={`lp-rank-node-state font-mono text-[9px] tracking-[0.2em] group-hover:text-[#D4AF37]/80 ${active ? "text-[#94A3B8]" : "text-[#475569]"}`}>
                      RANK {String(i * 5 + 1).padStart(2, "0")}
                    </span>
                  </div>
                );
              })}
            </div>
            <p className="mt-10 text-center font-mono text-[10px] tracking-[0.3em] uppercase text-[#64748B]" data-testid="rank-reveal-note">
              Full reveal at launch
            </p>
          </motion.div>
        </div>
      </section>

      {/* SECTION 9 — CERTIFICATES / ACHIEVEMENT RECOGNITION (formal certificate document) */}
      <section id="certificates" className="py-20 lg:py-28 relative overflow-hidden" data-testid="certificates-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <motion.div {...fadeUp} className="order-2 lg:order-1">
              <SealMark tier={tier} />
            </motion.div>
            <div className="order-1 lg:order-2">
              <SectionHeading
                eyebrow="Achievement Recognition"
                title="Achievement, Recognized."
                body="Every milestone in your PlusMaker journey earns formal recognition. Personalized certificates are issued within the member ecosystem — a lasting mark of progression."
                testId="certificates"
              />
              <motion.div
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-80px" }}
                variants={{ hidden: {}, show: { transition: { staggerChildren: 0.3, delayChildren: 0.4 } } }}
                className="mt-8 flex items-center gap-x-3 gap-y-2 flex-wrap"
                data-testid="certificate-flow"
              >
                {["ACHIEVEMENT", "RECOGNITION", "CERTIFICATE"].map((s, i) => (
                  <motion.span
                    key={s}
                    variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: "easeOut" } } }}
                    className="flex items-center gap-3"
                  >
                    <span className="font-mono text-[10px] tracking-[0.28em] text-[#D4AF37]">{s}</span>
                    {i < 2 && <span className="text-[#3a3d4d] text-xs">→</span>}
                  </motion.span>
                ))}
              </motion.div>
              <motion.p {...fadeUp} className="mt-7 font-mono text-[10px] tracking-[0.28em] uppercase text-[#64748B]" data-testid="certificates-tag">
                Member Recognition
              </motion.p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 10 — HOW PLUSMAKER WORKS */}
      <section className="py-20 lg:py-28 bg-[#0B0C10] relative" data-testid="how-it-works-section" ref={howRef}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="The Journey" title="How PlusMaker Works" testId="how" />
          <div className="mt-14 relative">
            <motion.div
              className="hidden lg:block absolute top-[26px] left-[12%] right-[12%] h-px origin-left bg-gradient-to-r from-transparent via-[#D4AF37]/40 to-transparent"
              style={{ scaleX: howLineScale }}
              data-testid="how-connector-line"
            />
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                ["01", "JOIN", "Enter through an authorized PlusMaker Introducing Broker."],
                ["02", "TRADE", "Build your trading journey through the ecosystem."],
                ["03", "PROGRESS", "Advance through the 36-rank progression."],
                ["04", "REDEEM", "Convert achievement into curated rewards."],
              ].map(([num, title, body], i) => (
                <motion.div
                  key={num}
                  {...fadeUp}
                  transition={{ duration: 0.6, delay: i * 0.18, ease: "easeOut" }}
                  className="relative text-center lg:text-left"
                  data-testid={`how-step-${i}`}
                >
                  <div className="mx-auto lg:mx-0 h-[52px] w-[52px] rounded-full border border-[#D4AF37]/40 bg-[#050506] flex items-center justify-center relative z-10">
                    <span className="font-mono text-sm text-[#D4AF37]">{num}</span>
                  </div>
                  <h3 className="mt-5 font-heading text-base font-semibold tracking-[0.15em] text-white">{title}</h3>
                  <p className="mt-2.5 text-sm text-[#94A3B8] leading-relaxed max-w-xs mx-auto lg:mx-0">{body}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 11 — THREE CORE PILLARS */}
      <section className="py-20 lg:py-28" data-testid="pillars-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {[
              { icon: CandlestickChart, title: "TRADING", body: "Precision-engineered market tools." },
              { icon: BrainCircuit, title: "AI", body: "Deeper market intelligence." },
              { icon: Gift, title: "REWARDS", body: "Achievement made tangible." },
            ].map((pillar, i) => (
              <motion.div
                key={pillar.title}
                initial={{ opacity: 0, y: 22, scale: 0.97 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, delay: 0.12 * i, ease: "easeOut" }}
                className="card-lux lp-pillar-card p-8 text-center"
                data-testid={`pillar-card-${i}`}
              >
                <div className="mx-auto h-14 w-14 rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/5 flex items-center justify-center mb-6">
                  <pillar.icon className="h-6 w-6 text-[#D4AF37]" />
                </div>
                <h3 className="font-heading text-base font-semibold tracking-[0.25em] text-white">{pillar.title}</h3>
                <div className="gold-divider w-12 mx-auto my-5" />
                <p className="text-sm text-[#94A3B8] leading-relaxed">{pillar.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 12 — PRIVATE MEMBERSHIP */}
      <section className="py-14 lg:py-20" data-testid="trust-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="card-lux px-8 py-10 sm:px-12 text-center">
            <p className="eyebrow-label mb-4">Private by Design</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-10 text-sm text-[#94A3B8]">
              <span className="flex items-center gap-2">
                <Lock className="h-4 w-4 text-[#D4AF37]" /> Referral-only membership
              </span>
              <span className="hidden sm:block h-4 w-px bg-[#222530]" />
              <span className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-[#D4AF37]" /> Exclusive to members within the PLUSMAKER network only
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SECTION 13 — FINAL CTA (Access) */}
      <section className="py-20 lg:py-28" data-testid="cta-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-90px" }}
            variants={{ hidden: {}, show: { transition: { staggerChildren: 0.16, delayChildren: 0.1 } } }}
            className="card-lux relative overflow-hidden p-10 sm:p-16 text-center gold-glow-frame"
          >
            <motion.div
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true, margin: "-90px" }}
              transition={{ duration: 1.3, ease: "easeInOut" }}
              className="absolute top-0 left-[16.7%] h-px w-2/3 lp-cta-line"
              data-testid="cta-gold-line"
            />
            <div className="absolute top-0 left-1/2 -translate-x-1/2 h-[220px] w-[520px] bg-[#D4AF37]/[0.07] blur-[110px] rounded-full pointer-events-none lp-ambient" />
            <div className="lp-scan" />
            <motion.p variants={staggerChild} className="eyebrow-label mb-5 relative">Invitation Only</motion.p>
            <motion.h2 variants={staggerChild} className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white relative" data-testid="cta-title">
              Your Trading Journey Starts Here.
            </motion.h2>
            <motion.p variants={staggerChild} className="mt-5 text-base md:text-lg text-[#94A3B8] max-w-2xl mx-auto leading-relaxed relative" data-testid="cta-body">
              Access the PlusMaker ecosystem through an authorized referral.
            </motion.p>
            <motion.div variants={staggerChild} className="mt-10 flex flex-col sm:flex-row gap-4 justify-center relative">
              <button
                onClick={() => navigate("/register")}
                data-testid="cta-register-button"
                className="btn-gold px-10 py-3.5 text-sm flex items-center justify-center gap-2"
              >
                Claim Your Access <ArrowRight className="h-4 w-4" />
              </button>
              <button
                onClick={() => navigate("/login")}
                data-testid="cta-login-button"
                className="btn-outline-gold px-10 py-3.5 text-sm"
              >
                Member Login
              </button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
